import { useMemo, useState } from 'react'
import { Button, FilterChip, SearchInput, Select } from '@quoter/web-core'
import {
  CATEGORIES,
  FODMAP_GROUPS,
  FOODS,
  LEVELS,
  filterFoods,
  type Category,
  type FodmapGroup,
  type Level,
} from '@/lib/foods'
import { useI18n } from '@/hooks/useI18n'
import { LevelDot, LevelPill } from './Level'

/**
 * The searchable food table. Used twice: inside the app, and on the public
 * /table page that anyone can open without an account — so it reads nothing
 * from the session.
 */
export function FoodTable() {
  const { t, fmt, locale } = useI18n()
  const [query, setQuery] = useState('')
  const [level, setLevel] = useState<Level | null>(null)
  const [category, setCategory] = useState<Category | null>(null)
  const [group, setGroup] = useState<FodmapGroup | null>(null)

  const foods = useMemo(
    () =>
      filterFoods(FOODS, { query, level, category, group }).sort(
        (a, b) => LEVELS.indexOf(a.level) - LEVELS.indexOf(b.level) || a[locale].localeCompare(b[locale], locale),
      ),
    [query, level, category, group, locale],
  )
  const filtered = !!query || !!level || !!category || !!group

  function clear() {
    setQuery('')
    setLevel(null)
    setCategory(null)
    setGroup(null)
  }

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.foods.search}
          aria-label={t.foods.search}
          wrapperClassName="h-12 bg-surface"
          className="text-[15px]"
        />
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip active={level === null} onClick={() => setLevel(null)}>
            {t.foods.allLevels}
          </FilterChip>
          {LEVELS.map((l) => (
            <FilterChip key={l} active={level === l} onClick={() => setLevel(level === l ? null : l)}>
              <LevelDot level={l} />
              {t.levels[l]}
            </FilterChip>
          ))}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Select
            value={category ?? ''}
            onChange={(e) => setCategory((e.target.value || null) as Category | null)}
            aria-label={t.foods.allCategories}
          >
            <option value="">{t.foods.allCategories}</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t.foods.categories[c]}
              </option>
            ))}
          </Select>
          <Select
            value={group ?? ''}
            onChange={(e) => setGroup((e.target.value || null) as FodmapGroup | null)}
            aria-label={t.foods.anyGroup}
          >
            <option value="">{t.foods.anyGroup}</option>
            {FODMAP_GROUPS.map((g) => (
              <option key={g} value={g}>
                {t.groups[g]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between text-[12.5px] text-gray-500">
        <span aria-live="polite">{fmt(t.foods.showing, { n: foods.length })}</span>
        {filtered && (
          <button type="button" onClick={clear} className="font-semibold text-brand-700 hover:underline">
            {t.foods.clear}
          </button>
        )}
      </div>

      {foods.length === 0 ? (
        <div className="rounded-xl border border-dashed border-strong bg-surface-muted px-6 py-10 text-center">
          <p className="text-[14px] text-gray-600">{t.foods.none}</p>
          <Button variant="secondary" size="sm" className="mt-4" onClick={clear}>
            {t.foods.clear}
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-surface shadow-card">
          {foods.map((food) => (
            <li key={food.id} className="flex flex-wrap items-start gap-x-4 gap-y-1.5 px-4 py-3 sm:px-5">
              <div className="min-w-0 flex-1 basis-56">
                <p className="text-[14.5px] font-semibold text-gray-900">{food[locale]}</p>
                <p className="text-[12px] text-gray-500">{t.foods.categories[food.category]}</p>
              </div>
              <div className="flex shrink-0 basis-24 items-center">
                <LevelPill level={food.level} />
              </div>
              <div className="min-w-0 flex-1 basis-64 text-[12.5px] leading-relaxed text-gray-600">
                {food.groups.length > 0 && (
                  <p className="font-semibold text-gray-700">{food.groups.map((g) => t.groups[g]).join(' · ')}</p>
                )}
                {food.note && <p>{t.foods.notes[food.note]}</p>}
                {food.groups.length === 0 && !food.note && <p className="text-gray-400">{t.foods.nothingToWatch}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <FoodLegend />
    </div>
  )
}

function FoodLegend() {
  const { t } = useI18n()
  const rows: [Level, string][] = [
    ['low', t.foods.legendLow],
    ['moderate', t.foods.legendModerate],
    ['high', t.foods.legendHigh],
  ]
  return (
    <div className="rounded-lg bg-surface-muted px-5 py-4">
      <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">{t.foods.legendTitle}</p>
      <ul className="mt-2.5 space-y-1.5">
        {rows.map(([level, text]) => (
          <li key={level} className="flex items-start gap-3 text-[13px] text-gray-700">
            <LevelPill level={level} className="mt-px shrink-0" />
            <span>{text}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[12px] leading-relaxed text-gray-500">{t.disclaimer}</p>
    </div>
  )
}
